import type { FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { doc, getDocFromServer, type Firestore } from 'firebase/firestore';
import { FAMILLES_SANS_PORTEE_RESTREINTE, auteurDeRequete, politiqueEffective, type PolitiquePortees } from '@taki/data-model';
import { PORTEES_MODELE } from './portees-modele.js';

/**
 * L'Auth de l'app, ou null quand l'app n'en porte pas. Sans cle API, `getAuth`
 * LEVE (`auth/invalid-api-key`) au lieu de dire « personne » : c'est le cas
 * d'une app de test des rules, dont l'identite vit dans le jeton de Firestore.
 */
export function authDe(app: FirebaseApp): Auth | null {
  return app.options.apiKey ? getAuth(app) : null;
}

/** Un jeu de la famille est-il restreint a son auteur, par le modele ou par la politique publiee ? */
function familleRestreinte(politique: PolitiquePortees, famille: string): boolean {
  return [politique, PORTEES_MODELE as PolitiquePortees].some(p => Object.values(p[famille] ?? {}).includes('owner-scoped'));
}

/**
 * La politique publiee d'UNE famille, `{ version: 1, types }`, absente si
 * aucune instance n'a choisi sa portee (`functions/src/ia/portees.ts`) :
 * `orgs/{orgId}/portees/{famille}` si elle restreint un type, sinon
 * `{famille}.ouverte`, qui ne compte (et ne se lit) que si le modele restreint
 * la famille. Rendue au format de `politiqueEffective`.
 */
export function idsDesPortees(famille: string): string[] {
  // Le fil d'echanges ne se restreint jamais : rien a lire ni a suivre.
  if (FAMILLES_SANS_PORTEE_RESTREINTE.includes(famille)) return [];
  return familleRestreinte({}, famille) ? [famille, `${famille}.ouverte`] : [famille];
}
function projectionDeFamille(famille: string, data: Record<string, unknown> | undefined): unknown {
  return data === undefined ? undefined : { version: data['version'], familles: { [famille]: data['types'] } };
}

/**
 * Lecture fraiche : une revocation ne survit pas dans un cache de session.
 * Une famille sans jeu restreint n'a pas d'auteur a fixer, quel que soit
 * l'appelant : ni session ni fiche membre a lire.
 */
export async function auteurPourRequete(db: Firestore, orgId: string, famille: string, type?: string): Promise<string | null> {
  if (FAMILLES_SANS_PORTEE_RESTREINTE.includes(famille)) return null;
  const lus = await Promise.all(idsDesPortees(famille).map(id => getDocFromServer(doc(db, 'orgs', orgId, 'portees', id))));
  const projection = lus.find(snap => snap?.exists());
  const politique = politiqueEffective(PORTEES_MODELE, projectionDeFamille(famille, projection?.data()));
  if (!familleRestreinte(politique, famille)) return null;
  const user = authDe(db.app)?.currentUser;
  if (!user) throw new Error('Une session est exigee pour lire les donnees');
  const member = user.email ? await getDocFromServer(doc(db, 'orgs', orgId, 'members', user.email.toLowerCase())) : null;
  let role: string;
  if (member?.exists()) {
    const fiche = member.data();
    if (fiche['disabled'] || fiche['archivedAt'] != null || fiche['deletedAt'] != null) throw new Error('Compte retire');
    role = String(fiche['orgRole']);
  } else role = String((await user.getIdTokenResult()).claims['orgRole'] ?? 'member');
  return auteurDeRequete(politique, famille, type, user.uid, role, PORTEES_MODELE);
}

export function cadrerParAuteur<T extends { filters: { field: string; value: unknown }[] }>(plan: T, auteur: string | null): T {
  if (auteur === null) return plan;
  if (plan.filters.some(f => f.field === 'createdBy' && f.value !== auteur)) throw new Error('Un filtre ne peut pas elargir la portee');
  return { ...plan, filters: [...plan.filters.filter(f => f.field !== 'createdBy'), { field: 'createdBy', value: auteur }] };
}
